import { FC, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import { t } from 'i18next';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { useSTKContext } from '@/context/context';
import styles from './PriceChangeConfirmation.module.css';
import { ModifierPriceChangeDrawerProps } from './ModifierPriceChangeDrawerProps.types';
import {
  getStoreGroupNamesWithExistingPrices,
  isModifierCategoryPriceUpdateOnly,
  isZeroPricedModifierSelection,
  modifierHasExistingPriceInStoreGroup,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';

export const ModifierPriceChangeConfirmation: FC<ModifierPriceChangeDrawerProps> = ({
  isOpen,
  onClose,
  onCancel,
  storeModifiers,
  afterSave,
  newPrice,
  categoryName,
}) => {
  const { storesAndGroups } = useSTKContext();
  const isPriceUpdateOnlyCategory = isModifierCategoryPriceUpdateOnly(categoryName);
  const isZeroPricedSelection = isZeroPricedModifierSelection(storeModifiers);

  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet).sort();
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      return (storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || []).sort();
    }

    return [];
  }, [storesAndGroups]);

  const visibleStoreGroupNames = useMemo(() => {
    if (!isPriceUpdateOnlyCategory) {
      return allStoreGroupNames;
    }

    return getStoreGroupNamesWithExistingPrices(storeModifiers, allStoreGroupNames);
  }, [allStoreGroupNames, isPriceUpdateOnlyCategory, storeModifiers]);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  const handleSubmit = () => {
    afterSave();
    onClose();
  };

  const handleCloseDrawer = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const formattedNewPrice = `$${parseFloat(newPrice || '0').toFixed(2)}`;
  const inactiveLabel = t('common.inactive');

  return (
    <SwipeableDrawer open={isOpen} onClose={handleCloseDrawer} onOpen={() => undefined} anchor="bottom">
      <div className={styles.container}>
        <header>
          <div>
            <h1>{t('editMenuPrice.activateEditMenuPrice')}</h1>
          </div>
          <button type="button" onClick={handleCloseDrawer}>
            <X data-testid="x-icon" size={24} color="var(--black-900)" />
          </button>
        </header>

        <div className={styles.content}>
          <div className={styles.confirmationContent}>
            <h2 className={styles.confirmationTitle}>{t('editMenuPrice.submitMenuChanges')}</h2>
            {isZeroPricedSelection && (
              <p className={styles.confirmationInfo}>{t('editMenuPrice.zeroPricedModifierConfirmationInfo')}</p>
            )}
            {isPriceUpdateOnlyCategory && (
              <p className={styles.confirmationInfo}>{t('editMenuPrice.modifierPriceUpdateOnlyConfirmationInfo')}</p>
            )}

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.productHeader}>{t('editMenuPrice.modifierColumn')}</th>
                    {visibleStoreGroupNames.map((storeGroupName) => {
                      const { count, total } = getStoreGroupsCount(storeGroupName);
                      return (
                        <th key={storeGroupName} className={styles.priceHeader}>
                          <div className={styles.headerWithCount}>
                            <div className={styles.headerName}>{storeGroupName}</div>
                            <div className={styles.headerCount}>
                              {count} / {total}
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {storeModifiers.map((modifier, index) => (
                    <tr key={`${modifier.modifierId}-${modifier.slotId}-${modifier.modifierWeightId}-${index}`}>
                      <td className={styles.productCell}>
                        <div className={styles.productColumnContent}>
                          <div className={styles.productName}>{modifier.modifierName}</div>
                          <div className={styles.productDescription}>{modifier.modifierDescription}</div>
                        </div>
                      </td>
                      {visibleStoreGroupNames.map((storeGroupName) => {
                        const showPrice =
                          !isPriceUpdateOnlyCategory || modifierHasExistingPriceInStoreGroup(modifier, storeGroupName);

                        return (
                          <td key={storeGroupName} className={styles.priceCell}>
                            {showPrice ? formattedNewPrice : <span className={styles.inactive}>{inactiveLabel}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.alertContainer}>
              <CalendarAlert requestedVerbiage={true} />
            </div>

            <div className={styles.buttonContainer}>
              <Button
                className={styles.cancelButton}
                onClick={handleCancel}
                variant={ButtonVariants.Secondary}
                type="button"
              >
                {t('editMenuPrice.cancel')}
              </Button>
              <Button
                className={styles.submitButton}
                onClick={handleSubmit}
                variant={ButtonVariants.Primary}
                type="button"
              >
                {t('editMenuPrice.submitChanges')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
};
